import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell, FlexBox, BusyIndicator, Text, Tag, MessageStrip } from '@ui5/webcomponents-react';
import { api } from '../../api/client';
import { PageHeader } from '../../components/Responsive';

/** Screen catalog: open the designer to build a new screen or edit an existing one. */
export function ScreensAdmin() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const screens = useQuery({ queryKey: ['screens-all'], queryFn: api.screens });

  const del = async (id: string) => {
    if (!window.confirm(`Delete screen "${id}"? This removes it from the menu.`)) return;
    try { await api.deleteScreen(id); qc.invalidateQueries({ queryKey: ['screens-all'] }); qc.invalidateQueries({ queryKey: ['screens'] }); }
    catch (e) { alert((e as Error).message); }
  };

  const fileRef = useRef<HTMLInputElement>(null);
  const download = (name: string, obj: unknown) => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
  };
  const exportOne = async (id: string) => { try { download(`screen-${id}.json`, await api.screen(id)); } catch (e) { alert((e as Error).message); } };
  const exportAll = async () => { try { const all = await Promise.all((screens.data ?? []).map(s => api.screen(s.id))); download('screens.json', all); } catch (e) { alert((e as Error).message); } };
  const importFile = async (file: File) => {
    try {
      const parsed = JSON.parse(await file.text());
      const defs = Array.isArray(parsed) ? parsed : [parsed];
      for (const d of defs) { if (!d.id || !d.entity) throw new Error('Each screen needs id and entity'); await api.saveScreen(d); }
      qc.invalidateQueries({ queryKey: ['screens-all'] }); qc.invalidateQueries({ queryKey: ['screens'] });
      alert(`Imported ${defs.length} screen(s)`);
    } catch (e) { alert(`Import failed: ${(e as Error).message}`); }
  };

  if (screens.isLoading) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;

  return (
    <div>
      <PageHeader title="Screens" actions={<>
        <input ref={fileRef} type="file" accept="application/json" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) importFile(f); e.target.value = ''; }} />
        <Button icon="upload" onClick={() => fileRef.current?.click()}>Import</Button>
        <Button icon="download" onClick={exportAll}>Export all</Button>
        <Button design="Emphasized" icon="add" onClick={() => nav('/admin/designer')}>New screen</Button>
      </>} />
      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '0.75rem' }}>
        Screens are defined visually in the Designer and stored in SAP (@B1LSCREEN). Anything you build here appears in the menu for the roles you allow.
      </MessageStrip>
      {/* Popin mode: on phones the narrower columns fold into each row instead of being cut off. */}
      <Table overflowMode="Popin" headerRow={<TableHeaderRow>
        <TableHeaderCell minWidth="9rem" importance={2}><span>Title</span></TableHeaderCell><TableHeaderCell minWidth="10rem"><span>Entity</span></TableHeaderCell><TableHeaderCell minWidth="9rem"><span>Menu</span></TableHeaderCell>
        <TableHeaderCell minWidth="7rem"><span>Roles</span></TableHeaderCell><TableHeaderCell minWidth="5rem"><span>Version</span></TableHeaderCell><TableHeaderCell minWidth="10rem" importance={1}><span></span></TableHeaderCell>
      </TableHeaderRow>}>
        {(screens.data ?? []).map(s => (
          <TableRow key={s.id}>
            <TableCell><span>{s.title}</span></TableCell><TableCell><Text style={{ color: 'var(--sapNeutralTextColor)' }}>{s.entity}</Text></TableCell>
            <TableCell><span>{s.menu?.group ?? '—'}{s.menu?.showOnHome ? ' · home' : ''}</span></TableCell>
            <TableCell>{s.roles?.length ? s.roles.map(r => <Tag key={r} colorScheme="6">{r}</Tag>) : <Text style={{ color: 'var(--sapNeutralTextColor)' }}>all</Text>}</TableCell>
            <TableCell><span>v{s.version}</span></TableCell>
            <TableCell><FlexBox style={{ gap: '0.25rem' }}><Button icon="edit" design="Transparent" onClick={() => nav(`/admin/designer/${s.id}`)}>Design</Button><Button icon="download" design="Transparent" onClick={() => exportOne(s.id)} /><Button icon="delete" design="Transparent" onClick={() => del(s.id)} /></FlexBox></TableCell>
          </TableRow>
        ))}
      </Table>
    </div>
  );
}
